import { withProdNamespace } from '@wix/editor-elements-conventions';

import {
  IComponentRegistry,
  IWebpackManifestContext,
  IRegistryManifestContext,
  IRegistryRuntime,
  ILibraryAssetsPatterns,
  IHostContext,
  IComponentBundleLoader,
  IRegistryParameters,
  IRegistryOptions,
} from '../../registry.types';
import {
  createException,
  RegistryError,
  RegistryErrorCode,
} from '../../errors/RegistryError';

import {
  loadLibrariesManifests,
  parseManifests,
  isRegistryManifestContext,
} from '../../libraries';
import { createResourceLoader } from '../../loaders/createResourceLoader';
import { createEagerEntry } from './createEagerEntry';
import { createLazyEntry } from './createLazyEntry';

const DEFAULT_MODE = 'lazy';

export interface ICreateEntryParameters {
  host: string;
  libraryAssetsPatterns?: ILibraryAssetsPatterns;
}

function getRegistryRuntime({
  manifests,
}: {
  manifests: Array<IWebpackManifestContext | IRegistryManifestContext>;
}): IRegistryRuntime {
  const shouldReturnRuntime = manifests.every(manifest =>
    isRegistryManifestContext(manifest),
  );

  return shouldReturnRuntime
    ? {
        libraries: manifests as Array<IRegistryManifestContext>,
      }
    : null;
}

export function withDefaultOptions(
  options: IRegistryOptions,
): Required<IRegistryOptions> {
  return {
    dev: false,
    useNewManifest: true,
    shouldLoadCSSAssets: false,
    useScriptsInsteadOfEval: false,
    ...options,
  };
}

export function createEntry<
  TComponentModel,
  TParts extends Extract<keyof TComponentModel, string> = never,
>({
  host,
  libraryAssetsPatterns = [],
}: ICreateEntryParameters): IComponentRegistry<TComponentModel, TParts> {
  return async params => {
    const {
      libraries,
      transport,
      shouldLoadCSSAssets,
      useScriptsInsteadOfEval,
    } = params;

    const options = withDefaultOptions({
      shouldLoadCSSAssets,
      useScriptsInsteadOfEval,
      ...(params.options || {}),
    });

    if (!libraries || !libraries.length) {
      throw createException(
        '"libraries" is required property to create the registry',
        RegistryErrorCode.IncorrectEntryArguments,
      );
    }

    if (!params.registerComponent && !params.registerLibrary) {
      throw createException(
        '"registerComponent" or "registerLibrary" should be passed to the registry',
        RegistryErrorCode.IncorrectEntryArguments,
      );
    }

    try {
      const resourceLoader = createResourceLoader({
        useScriptsInsteadOfEval: options.useScriptsInsteadOfEval,
        transport,
        shouldLoadCSSAssets: options.shouldLoadCSSAssets,
        globals: params.globals,
      });

      const manifests = await loadLibrariesManifests({
        host,
        resourceLoader,
        libraries,
        options,
      });

      const contexts = parseManifests({
        host,
        manifests,
        libraryAssetsPatterns,
      });

      const bundleLoaders: Record<string, IComponentBundleLoader> = {};

      contexts.forEach(({ library }) => {
        bundleLoaders[library.artifactId] = resourceLoader.createBundleLoader({
          environment: library.environment,
        });
      });

      const getModeByNamespace = (namespace?: string) => {
        const mode = params.mode || DEFAULT_MODE;
        if (!namespace || !params.modes) {
          return mode;
        }

        const modeAtNamespace =
          params.modes && params.modes[withProdNamespace(namespace)];

        return modeAtNamespace ? modeAtNamespace : mode;
      };

      const eagerContexts = contexts.filter(context => {
        return getModeByNamespace(context.library.namespace) === 'eager';
      });

      const lazyContexts = contexts.filter(context => {
        return getModeByNamespace(context.library.namespace) === 'lazy';
      });

      const runLazyRegistry = (
        hosts: Array<IHostContext>,
        props: IRegistryParameters<TComponentModel, TParts>,
      ) => {
        if (!hosts.length) {
          return;
        }

        const lazyRegistry = createLazyEntry<TComponentModel, TParts>({
          contexts: hosts,
          bundleLoaders,
        });

        return lazyRegistry(props);
      };

      const runEagerRegistry = async (
        hosts: Array<IHostContext>,
        props: IRegistryParameters<TComponentModel, TParts>,
      ) => {
        if (!hosts.length) {
          return;
        }

        const eagerRegistry = createEagerEntry<TComponentModel>({
          contexts: hosts,
          bundleLoaders,
        });

        return eagerRegistry(props as any);
      };

      await Promise.all([
        runLazyRegistry(lazyContexts, params),
        runEagerRegistry(eagerContexts, params),
      ]);

      return getRegistryRuntime({ manifests });
    } catch (e) {
      if (options.dev && !(e instanceof RegistryError)) {
        console.error(
          `Registry Unexpected Exception – "${e.message}".
Please notify registry developers about this error.`,
        );
      }

      throw e;
    }
  };
}

export default createEntry;
